// Reusable CardConnect sales-code / portfolio picker.
//
// There is no other in-site way to set the sales code -- Auto (the empty
// value) lets the backend route by the merchant's monthly volume, and the
// explicit options pin the application to a specific WILMA portfolio (#64
// follow-up: the intercepted-intake case must land on CCIP-WILMA-AI2).
//
// By default the picker surfaces only the four Wilma portfolios; the
// four SSTHUB salesperson-attribution portfolios the backend also accepts
// (sales_code_routing.ALLOWED_SALES_CODES) are opt-in via
// includeAllPortfolios, for callers that need to route a specific
// merchant to one of them. The option list + the paired template lookup
// live in lib/salesCode.ts (a TS mirror of sales_code_routing.py) so this
// component stays a thin presentational control shared by NewApplication
// and EditApplicationModal.

import { salesCodeOptions } from "../lib/salesCode";
import { FieldErrorText } from "./application/FormField";

interface Props {
  id?: string;
  /** "" = Auto; otherwise one of the WILMA/SSTHUB sales codes. */
  value: string;
  onChange: (value: string) => void;
  /** Inline error text (rendered in place of the hint when set). */
  error?: string;
  disabled?: boolean;
  /** Override the default helper text under the control. */
  hint?: string;
  /** Also offer the four SSTHUB salesperson-attribution portfolios. */
  includeAllPortfolios?: boolean;
}

const DEFAULT_HINT =
  "Auto routes to Wilma AI2 at/above $700K/mo average volume, Wilma AI " +
  "below. Pick a portfolio to override.";

export function SalesCodeSelect({
  id = "salesCode",
  value,
  onChange,
  error,
  disabled,
  hint = DEFAULT_HINT,
  includeAllPortfolios = false,
}: Props) {
  const errId = `${id}-err`;
  return (
    <div>
      <label htmlFor={id} className="label mb-1.5 block">
        CardConnect sales code
      </label>
      <select
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? errId : undefined}
      >
        {salesCodeOptions({ includeAllPortfolios }).map((o) => (
          <option key={o.value || "auto"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? (
        <FieldErrorText id={errId}>{error}</FieldErrorText>
      ) : (
        <p className="hint">{hint}</p>
      )}
    </div>
  );
}
